import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import { usersApi } from "@/lib/humhub-api/users";
import { queryInvalidationHelpers } from "@/lib/queryInvalidationHelpers";
import { updateMembershipBlockStatus } from "@/lib/updateMembershipBlockStatus";

export const userQueryKeys = {
  all: ["users"] as const,
  current: () => [...userQueryKeys.all, "current"] as const,
  byId: (id: number) => [...userQueryKeys.all, "detail", id] as const,
  profile: () => [...userQueryKeys.all, "profile"] as const,
  blocked: () => [...userQueryKeys.all, "blocked"] as const,
};

export interface UseBlockUserOptions {
  invalidateUserInfo?: boolean;
}

export const useBlockUser = ({
  invalidateUserInfo = false,
}: UseBlockUserOptions = {}) => {
  const queryClient = useQueryClient();

  const blockUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      return usersApi.blockUser(userId);
    },
    onMutate: async (userId) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: userQueryKeys.blocked() });

      // Snapshot the previous value
      const previousBlocked = queryClient.getQueryData(userQueryKeys.blocked());

      // Optimistically update to the new value
      queryClient.setQueryData<string[]>(userQueryKeys.blocked(), (old) => {
        if (Array.isArray(old)) {
          return [...old, String(userId)];
        }
        return old ?? [String(userId)];
      });

      // Return a context object with the snapshotted value
      return { previousBlocked };
    },
    onError: (err, _userId, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousBlocked) {
        queryClient.setQueryData(
          userQueryKeys.blocked(),
          context.previousBlocked,
        );
      }
      console.error("useBlockUser: Error blocking user:", err);
    },
    onSuccess: (_success, userId) => {
      if (typeof userId === "number") {
        // UIを即時更新するためにキャッシュを先に書き換える
        updateMembershipBlockStatus(queryClient, userId, true);
        queryInvalidationHelpers.invalidateOnUserBlockChange(queryClient, {
          userId,
          invalidateUserInfo,
        });
      }
    },
    onSettled: () => {
      // Always refetch after error or success to ensure server state
      queryClient.invalidateQueries({ queryKey: userQueryKeys.blocked() });
    },
  });

  const blockUser = useCallback(
    async (userId: number) => {
      return blockUserMutation.mutateAsync(userId);
    },
    [blockUserMutation],
  );

  return {
    blockUser,
    isBlocking: blockUserMutation.isPending,
    blockError: blockUserMutation.error,
    blockSuccess: blockUserMutation.isSuccess,
  };
};
